var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * The org's audit log, as a page inside the organization profile.
 *
 * WHAT IT IS FOR. "Who granted that, and when" is the question asked after an
 * incident or during a procurement review, and until now the answer lived in a
 * container log that rotates and no customer can query. This is the surface that
 * answers it, sitting beside Members and Permissions because it is the record of
 * what was done on those two pages.
 *
 * APPEND-ONLY, deliberately. There is no delete control here because there is no
 * delete verb behind it: this is the record of what everyone did, including the
 * admins reading it, and a record its own subjects can edit is not a record.
 *
 * SCOPED SERVER-SIDE. The page asks for the log and gets its own org's, matched
 * against the org stamped on each event when it happened — so a gateway that
 * changed hands does not carry its previous owner's history into this list. The
 * browser does no filtering, and could not be trusted to.
 */
import { html, nothing, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { VirtualRows } from './virtual-rows';
import { t } from './i18n';
import { fmtDateTime } from './format';
/*
 * One entry in the org's audit log.
 *
 * Declared HERE rather than imported from a transport module: the page renders
 * the org's history and does not care which service served it. The store is a
 * Cloudflare Worker (D1 hot, R2 cold) rather than the NATS fabric — audit reads
 * are not a relay's job, and this record outlives any individual gateway.
 */
/** Event kinds the identity provider never attributes. Not a guess about missing
 *  data: these arrive from webhooks whose payload has no actor field at all. */
const PROVIDER_UNATTRIBUTED = new Set(['member.invited']);
/*
 * The audit log's host. One absolute URL for every surface, deliberately.
 *
 * An edge box reads the org's history from the same store the cloud app does,
 * because that history belongs to the ORG and not to any gateway: an org can
 * exist with no hardware at all, and a box changing hands must not carry a
 * company's records off with it. A platform constant, never configuration.
 */
export const AUDIT_URL = 'https://audit.vitesseautomation.ai';
/*
 * The read, written ONCE.
 *
 * base-ui still holds no transport: the caller supplies the token, exactly as the
 * Connect helpers take a Transport the caller built. What lives here is the WIRE
 * SHAPE — the path, the header, how a failure becomes a thrown message — which is
 * the thing that goes subtly wrong in a different way in each copy.
 *
 * The ORG IS NOT SENT. The worker derives it from the verified session, so a
 * client cannot widen its own scope; one that named another org would be refused
 * rather than quietly narrowed.
 */
export async function fetchAuditEvents(getToken, limit = 200) {
    const token = await getToken();
    if (!token)
        return [];
    const res = await fetch(`${AUDIT_URL}/events?limit=${limit}`, {
        headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        // Thrown, not swallowed into an empty list: on screen those look identical and
        // only one of them means "nothing happened".
        throw new Error(`audit log unavailable (${res.status})`);
    }
    const body = (await res.json());
    return (body.events ?? []).map(fromWire);
}
function fromWire(e) {
    return {
        id: e.id,
        atMs: e.atMs,
        kind: e.kind,
        actorUserId: e.actorUserId ?? '',
        actorLabel: e.actorLabel ?? '',
        subject: e.subject ?? '',
        detail: e.detail ? JSON.stringify(e.detail) : '',
    };
}
/* Matching the stroke-width and 24-box of Clerk's own nav icons so the row does
 * not look pasted in beside General, Members and Permissions. */
const LOG_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>' +
    '<path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>';
export function auditPage(opts) {
    let panel;
    return {
        label: t('audit.title'),
        url: 'audit',
        mountIcon: (el) => {
            el.innerHTML = LOG_ICON;
        },
        unmountIcon: () => { },
        mount: (el) => {
            // Same remount hazard as the billing page: Clerk can call mount on a container still
            // holding the previous mount's children. Replace them whole.
            el.replaceChildren();
            panel = document.createElement('vitesse-audit-panel');
            panel.load = opts.events;
            if (opts.nameFor)
                panel.nameFor = opts.nameFor;
            el.appendChild(panel);
        },
        unmount: () => {
            panel?.remove();
            panel = undefined;
        },
    };
}
/* The chosen view survives the provider's post-open remount, same as the billing page's pills:
 * a filter that silently reset read as the page reloading itself. */
let warmAuditView = 'all';
let VitesseAuditPanel = class VitesseAuditPanel extends LitElement {
    constructor() {
        super(...arguments);
        /* The audit log is the one list here with no ceiling on its length, so it is the
         * one that windows. 56px is the inventory row's height; overscan keeps a fast
         * flick from showing a blank band. */
        this.vrows = new VirtualRows(this, { estimateSize: 56, overscan: 8 });
        this.events = [];
        /*
         * THE VIEW FILTER — the same top-of-page pills the billing page grew, over one list.
         *
         * Families rather than raw kinds: the kinds are producer strings (plan.upgraded,
         * rbac.org_roles_changed, org.renamed) and a reader thinks in questions — what happened to the
         * money, to access, to the organisation itself. Prefix-classified so a NEW kind lands in a
         * family by construction instead of silently joining "everything".
         */
        this.view = warmAuditView;
        this.error = '';
        this.loading = true;
    }
    // Light DOM: the panel inherits the app's tokens and base classes rather than
    // carrying a second copy of them behind a shadow boundary.
    createRenderRoot() {
        return this;
    }
    connectedCallback() {
        super.connectedCallback();
        void this.refresh();
    }
    async refresh() {
        if (!this.load)
            return;
        this.loading = true;
        try {
            this.events = await this.load();
            this.vrows.setCount(this.events.length);
            this.error = '';
        }
        catch (e) {
            // Surfaced rather than swallowed: an empty list and a failed read look
            // identical, and only one of them means "nothing happened".
            this.error = e instanceof Error ? e.message : String(e);
        }
        finally {
            this.loading = false;
        }
    }
    actor(ev) {
        /*
         * NO ACTOR IS NOT "THE SYSTEM".
         *
         * Some sources genuinely do not say who acted — the identity provider's
         * organization.updated webhook, for one, carries no actor at all. Labelling
         * that "System" asserts something the record does not contain: it reads as
         * "an automated process did this", when the truth is "a person did this and we
         * were not told which". The honest label says the second.
         */
        if (ev.actorUserId === '') {
            /*
             * WHICH KIND OF BLANK IT IS.
             *
             * "Actor not recorded" is true but leaves the reader wondering whether we
             * lost it. For an invitation we know exactly why: the provider's
             * `organizationInvitation.created` payload carries the invitation and not
             * who raised it, so there was never an actor to keep. Saying that turns a
             * gap that looks like a defect into a stated limit of the source.
             */
            return PROVIDER_UNATTRIBUTED.has(ev.kind) ? t('audit.actorUnknownProvider') : t('audit.actorUnknown');
        }
        // The label recorded AT THE TIME wins over a current lookup: the log should
        // read the way it read when it was written.
        /*
         * AND IT NEVER FALLS BACK TO THE ID.
         *
         * A producer that does not stamp actorLabel — plans-worker does not — left this
         * on the roster lookup, and the roster is not loaded on the Audit tab, so a real
         * row read "Plan upgraded · user_3EvOADudiOkBAPrXScM6VllbnT7". The same person
         * appeared by name one row above, because fleet-control DOES stamp the label,
         * which makes the log look broken rather than incomplete.
         *
         * Same rule as the subject: a name we cannot resolve is said in words. The id
         * is still in the record for anyone querying the store; it is the READER who
         * is not served by it.
         */
        return ev.actorLabel || this.nameFor?.(ev.actorUserId) || t('audit.actorUnknown');
    }
    /*
     * ONE SENTENCE PER EVENT, in the same inventory row as every other panel here.
     *
     * The row used to print the raw record: the org id as the title, then the actor,
     * a full timestamp and the payload JSON run together with dots. Every part of
     * that is true and none of it is readable — the id is the same on every row, so
     * the one thing that DID differ (what changed) was the raw JSON at the end.
     *
     * So: what happened is the title, in words; who and when is the line under it;
     * and the payload is dropped rather than printed, because a person reading their
     * org's history is not debugging a webhook. What the payload adds that words
     * cannot — the new name — is folded into the sentence instead.
     */
    familyOf(kind) {
        if (kind.startsWith('plan') || kind.startsWith('billing') || kind.startsWith('credit'))
            return 'billing';
        if (kind.startsWith('rbac') ||
            kind.startsWith('member') ||
            kind.startsWith('user') ||
            kind.startsWith('invitation') ||
            kind.startsWith('role') ||
            kind.startsWith('permission') ||
            kind.startsWith('seats'))
            return 'access';
        return 'org';
    }
    get visible() {
        if (this.view === 'all')
            return this.events;
        return this.events.filter((e) => this.familyOf(e.kind) === this.view);
    }
    render() {
        if (this.loading)
            return html `<div class="base-field-hint">${t('audit.loading')}</div>`;
        if (this.error)
            return html `<div class="base-form-error">${this.error}</div>`;
        if (this.events.length === 0)
            return html `<div class="base-field-hint">${t('audit.empty')}</div>`;
        const shown = this.visible;
        return html `
      <div class="base-tab-list" role="tablist">
        ${[
            ['all', t('audit.viewAll')],
            ['billing', t('audit.viewBilling')],
            ['access', t('audit.viewAccess')],
            ['org', t('audit.viewOrg')],
        ].map(([id, label]) => html `<button
            class="base-tab"
            role="tab"
            aria-selected=${this.view === id ? 'true' : 'false'}
            @click=${() => {
            this.view = id;
            warmAuditView = id;
            this.vrows.setCount(this.visible.length);
        }}
          >
            ${label}
          </button>`)}
      </div>
      <p class="base-field-hint org-page-hint">${t('audit.viewHint')}</p>
      ${shown.length === 0 ? html `<div class="base-field-hint">${t('audit.emptyView')}</div>` : nothing}
      <div class="base-inventory-list base-inventory-list--scroll" ${ref(this.vrows.attach)}>
        <div class="base-inventory-spacer" style=${`height:${this.vrows.totalSize}px`}>
        ${this.vrows.rows.map(({ index, start, size }) => {
            const ev = shown[index];
            if (!ev)
                return nothing;
            return html `
            <div
              class="base-inventory-row base-inventory-row--virtual"
              data-index=${String(index)}
              style=${`transform:translateY(${start}px);height:${size}px`}
            >
              <span class="base-inventory-ident">
                <span class="rbac-avatar" aria-hidden="true">
                  <vitesse-icon name=${iconFor(ev.kind)} size="14"></vitesse-icon>
                </span>
                <span class="base-inventory-titles">
                  <span class="base-inventory-name">${this.headline(ev)}</span>
                  <span class="base-inventory-sub">${this.actor(ev)} · ${fmtDateTime(ev.atMs)}</span>
                </span>
              </span>
            </div>
          `;
        })}
        </div>
      </div>
    `;
    }
    /** The event as a sentence. An unknown kind falls back to the kind itself
     *  rather than to nothing — a row that says `member.suspended` is worse than
     *  one that says "Member joined" and better than a blank line. */
    headline(ev) {
        /*
         * NO RAW IDENTIFIERS, EVER — a row a person cannot read is not a record.
         *
         * Two ways this used to leak them. A kind with no sentence fell back to the
         * kind itself, so the log showed `rbac.org_roles_changed`. And the {{name}}
         * slot fell back to `ev.subject`, which for an RBAC event IS a Clerk user id —
         * so the best case printed "user_3EvOA…'s permissions changed".
         *
         * The subject is resolved through the same roster the actor uses, and a kind
         * with no sentence now reads as a plain description of its own words rather
         * than its wire name.
         */
        const key = `audit.kind.${ev.kind.replace(/[^A-Za-z0-9]+/g, '_')}`;
        const out = t(key, { name: this.subjectName(ev) });
        return out === key ? prettyKind(ev.kind) : out;
    }
    /**
     * The subject as a person would say it.
     *
     * An RBAC event's subject is a user id, and the roster is the only thing that
     * can turn it into a name — the same lookup the actor already uses, so the two
     * halves of a row cannot disagree about what to call somebody. A payload name
     * wins when there is one, because it is what was true AT THE TIME.
     */
    subjectName(ev) {
        const fromDetail = detailName(ev.detail, ev.kind);
        if (fromDetail)
            return fromDetail;
        if (!ev.subject)
            return t('audit.subjectUnknown');
        // A Clerk id is never shown: an unresolved one says so instead.
        if (/^(user|org)_/.test(ev.subject))
            return this.nameFor?.(ev.subject) || t('audit.subjectUnknown');
        return ev.subject;
    }
};
__decorate([
    property({ attribute: false })
], VitesseAuditPanel.prototype, "load", void 0);
__decorate([
    property({ attribute: false })
], VitesseAuditPanel.prototype, "nameFor", void 0);
__decorate([
    state()
], VitesseAuditPanel.prototype, "events", void 0);
__decorate([
    state()
], VitesseAuditPanel.prototype, "view", void 0);
__decorate([
    state()
], VitesseAuditPanel.prototype, "error", void 0);
__decorate([
    state()
], VitesseAuditPanel.prototype, "loading", void 0);
VitesseAuditPanel = __decorate([
    customElement('vitesse-audit-panel')
], VitesseAuditPanel);
export { VitesseAuditPanel };
/**
 * A kind with no sentence, said in words rather than in its wire name.
 *
 * `rbac.org_roles_changed` becomes "Rbac org roles changed" — not elegant, and
 * enormously better than the identifier, which reads as a bug to anybody who is
 * not us. A new kind therefore degrades to something legible instead of leaking
 * the contract, and the missing translation is still obvious to whoever adds it.
 */
function prettyKind(kind) {
    const words = kind.replace(/[._-]+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : kind;
}
/** The human-meaningful field out of a payload, when there is one. The payload is
 *  the provider's shape, so this reads the couple of keys that carry a NAME and
 *  ignores the rest rather than pretty-printing the whole object. */
function detailName(detail, kind = '') {
    if (!detail)
        return '';
    /*
     * WHOSE NAME THE SENTENCE WANTS depends on the kind, and reading one fixed key
     * order for every kind is what produced two wrong sentences:
     *
     *   "Vitesse joined"        — `organization` won, so the ORG was named as the
     *                             person who joined. It was Luc who joined Vitesse.
     *   "org:admin was invited" — `role` won, so an invitation was labelled by the
     *                             role rather than by the person invited.
     *
     * A member/user/invitation event is ABOUT A PERSON, so it takes identifying keys
     * only — an email or a personal name — and never falls back to the organisation
     * or the role, neither of which is a who. Everything else (org renamed, and the
     * org-shaped events) still wants the organisation's name.
     */
    const personKeys = ['email_address', 'email', 'identifier', 'user_name', 'first_name'];
    const orgKeys = ['name', 'organization', 'role', 'email'];
    const aboutAPerson = /^(member|user|invitation|rbac_user)/.test(kind);
    try {
        const o = JSON.parse(detail);
        for (const k of aboutAPerson ? personKeys : orgKeys) {
            const v = o[k];
            if (typeof v === 'string' && v)
                return v;
        }
    }
    catch {
        /* not JSON — nothing to pull out, and the sentence reads fine without it */
    }
    return '';
}
/** The mark on the row: what KIND of thing changed, from the icons this library
 *  already ships. Deliberately coarse — four buckets a reader can tell apart at a
 *  glance beat a unique glyph per event kind that only the author can decode. */
function iconFor(kind) {
    if (kind.startsWith('member') || kind.startsWith('user'))
        return 'user';
    if (kind.startsWith('invitation'))
        return 'mail';
    if (kind.startsWith('role') || kind.startsWith('permission') || kind.startsWith('seats'))
        return 'shield-user';
    return 'sparkles';
}
//# sourceMappingURL=org-audit-page.js.map